import { Calendar, MapPin, Users, Clock, ChevronDown, Minus, Plus, CalendarIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { FilterState } from "@/pages/Reports";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { listObras } from "@/lib/obraCache";

interface Reports4FiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
}

// Obras reais (obraCache hidratado do Supabase), com cor estável por posição
const OBRA_COLORS = ['#0071CE', '#28A745', '#FFC107', '#DC3545', '#6F42C1', '#20C997'];
const getObrasForFilter = () =>
  listObras().map((obra, i) => ({
    id: obra.id,
    name: obra.nomeObra || "Obra sem nome",
    color: OBRA_COLORS[i % OBRA_COLORS.length],
  }));

const mockResponsaveis = [
  { id: 'user1', name: 'João Silva', avatar: 'JS' },
  { id: 'user2', name: 'Maria Santos', avatar: 'MS' },
  { id: 'user3', name: 'Pedro Oliveira', avatar: 'PO' },
  { id: 'user4', name: 'Ana Costa', avatar: 'AC' },
];

export const Reports4Filters = ({ filters, onFiltersChange }: Reports4FiltersProps) => {
  const [showPersonalizado, setShowPersonalizado] = useState(false);
  const [dataInicial, setDataInicial] = useState<Date>();
  const [dataFinal, setDataFinal] = useState<Date>();
  const [searchObras, setSearchObras] = useState("");

  const obrasReais = getObrasForFilter();

  // Filtrar obras baseado na busca
  const filteredObras = obrasReais.filter(obra =>
    obra.name.toLowerCase().includes(searchObras.toLowerCase())
  );

  const handleObraToggle = (obraId: string, checked: boolean) => {
    const newObras = checked
      ? [...filters.obras, obraId]
      : filters.obras.filter(id => id !== obraId);
    
    onFiltersChange({
      ...filters,
      obras: newObras,
    });
  };

  const handleResponsavelToggle = (userId: string, checked: boolean) => {
    const newResponsaveis = checked
      ? [...filters.responsaveis, userId]
      : filters.responsaveis.filter(id => id !== userId);
    
    onFiltersChange({
      ...filters,
      responsaveis: newResponsaveis,
    });
  };

  const handlePeriodChange = (tipo: 'hoje' | 'estaSemana' | 'esteMes' | 'esteAno' | 'ultimos30' | 'ultimos12meses' | 'todoPeriodo' | 'personalizado') => {
    if (tipo === 'personalizado') {
      setShowPersonalizado(true);
    } else {
      setShowPersonalizado(false);
      onFiltersChange({
        ...filters,
        periodo: { tipo },
      });
    }
  };

  const handlePersonalizadoApply = () => {
    if (dataInicial && dataFinal) {
      onFiltersChange({
        ...filters,
        periodo: { 
          tipo: 'personalizado',
          inicio: dataInicial,
          fim: dataFinal
        },
      });
      setShowPersonalizado(false);
    }
  };

  const getPeriodLabel = () => {
    switch (filters.periodo.tipo) {
      case 'hoje': return 'Hoje';
      case 'estaSemana': return 'Esta Semana';
      case 'esteMes': return 'Este mês';
      case 'esteAno': return 'Este ano';
      case 'ultimos30': return 'Últimos 30 dias';
      case 'ultimos12meses': return 'Últimos 12 meses';
      case 'todoPeriodo': return 'Todo o período';
      case 'personalizado': 
        if (filters.periodo.inicio && filters.periodo.fim) {
          return `${format(filters.periodo.inicio, 'dd/MM/yyyy')} - ${format(filters.periodo.fim, 'dd/MM/yyyy')}`;
        }
        return 'Período Personalizado';
      default: return 'Selecionar período';
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-4">
      {/* Obra Filter - Conforme Relatórios 3 */}
      <Card className="p-3 bg-background/50 backdrop-blur border-muted">
        <div className="flex items-center gap-3">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" className="flex min-h-11 items-center gap-2 p-0 font-normal sm:min-h-0">
                {filters.obras.length === 0 ? (
                  "Todas as obras"
                ) : (
                  <div className="flex items-center gap-1">
                    <span>{filters.obras.length} obras</span>
                    <div className="flex gap-1">
                      {filters.obras.slice(0, 3).map(obraId => {
                        const obra = obrasReais.find(o => o.id === obraId);
                        return obra ? (
                          <Badge 
                            key={obra.id} 
                            className="text-xs px-2 py-1"
                            style={{ backgroundColor: obra.color, color: 'white' }}
                          >
                            {obra.name.split(' ')[0]}
                          </Badge>
                        ) : null;
                      })}
                      {filters.obras.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{filters.obras.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Selecionar Obras</h4>
                
                {/* Campo de busca */}
                <Input
                  placeholder="Buscar obras..."
                  value={searchObras}
                  onChange={(e) => setSearchObras(e.target.value)}
                  className="text-sm"
                />
                
                {/* Primeiro item sempre é "Todas as obras" */}
                <div className="flex items-center space-x-2 p-2 border rounded">
                  <Checkbox
                    id="all-obras"
                    checked={filters.obras.length === 0}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        onFiltersChange({
                          ...filters,
                          obras: [],
                        });
                      }
                    }}
                  />
                  <label htmlFor="all-obras" className="text-sm font-medium">
                    Todas as obras
                  </label>
                </div>
                <div className="border-t pt-2 space-y-2 max-h-48 overflow-y-auto">
                  {filteredObras.length > 0 ? (
                    filteredObras.map((obra) => (
                    <div key={obra.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={obra.id}
                        checked={filters.obras.includes(obra.id)}
                        onCheckedChange={(checked) =>
                          handleObraToggle(obra.id, checked as boolean)
                        }
                      />
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: obra.color }}
                      />
                      <label
                        htmlFor={obra.id}
                        className="text-sm font-medium leading-none"
                      >
                        {obra.name}
                      </label>
                    </div>
                  ))
                  ) : (
                    <div className="text-sm text-muted-foreground text-center py-4">
                      Nenhuma obra encontrada
                    </div>
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </Card>

      {/* Responsaveis Filter - Com ícone de seta para baixo */}
      <Card className="p-3 bg-background/50 backdrop-blur border-muted">
        <div className="flex items-center gap-3">
          <Users className="h-4 w-4 text-muted-foreground" />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" className="flex min-h-11 items-center gap-2 p-0 font-normal sm:min-h-0">
                {filters.responsaveis.length === 0 ? (
                  "Todos os responsáveis"
                ) : (
                  <div className="flex items-center gap-1">
                    <span>{filters.responsaveis.length} selecionados</span>
                    <div className="flex gap-1">
                      {filters.responsaveis.slice(0, 3).map(userId => {
                        const user = mockResponsaveis.find(u => u.id === userId);
                        return user ? (
                          <div
                            key={user.id}
                            className="w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center"
                          >
                            {user.avatar}
                          </div>
                        ) : null;
                      })}
                      {filters.responsaveis.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{filters.responsaveis.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64">
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Responsáveis</h4>
                {mockResponsaveis.map((responsavel) => (
                  <div key={responsavel.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={responsavel.id}
                      checked={filters.responsaveis.includes(responsavel.id)}
                      onCheckedChange={(checked) =>
                        handleResponsavelToggle(responsavel.id, checked as boolean)
                      }
                    />
                    <div className="w-8 h-8 rounded-full bg-primary text-white text-sm flex items-center justify-center">
                      {responsavel.avatar}
                    </div>
                    <label
                      htmlFor={responsavel.id}
                      className="text-sm font-medium leading-none"
                    >
                      {responsavel.name}
                    </label>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </Card>

      {/* Period Filter - Menu suspenso */}
      <Card className="p-2 bg-background/50 backdrop-blur border-muted">
        <div className="flex items-center gap-2">
          <Minus className="h-4 w-4 text-muted-foreground" />
          <Popover open={showPersonalizado} onOpenChange={setShowPersonalizado}>
            <PopoverTrigger asChild>
              <div>
                <Select 
                  value={filters.periodo.tipo} 
                  onValueChange={(value) => {
                    if (value === 'personalizado') {
                      setShowPersonalizado(true);
                    } else {
                      setShowPersonalizado(false);
                      onFiltersChange({
                        ...filters,
                        periodo: { tipo: value as any },
                      });
                    }
                  }}
                >
                  <SelectTrigger className="h-11 w-[140px] border-0 bg-transparent p-0 sm:h-auto">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hoje">Hoje</SelectItem>
                    <SelectItem value="estaSemana">Esta Semana</SelectItem>
                    <SelectItem value="esteMes">Este mês</SelectItem>
                    <SelectItem value="esteAno">Este ano</SelectItem>
                    <SelectItem value="ultimos30">Últimos 30 dias</SelectItem>
                    <SelectItem value="ultimos12meses">Últimos 12 meses</SelectItem>
                    <SelectItem value="todoPeriodo">Todo o período</SelectItem>
                    <SelectItem value="personalizado">Período Personalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </PopoverTrigger>
            
            {/* Menu suspenso personalizado */}
            <PopoverContent className="w-72 p-4 z-50" align="start" side="bottom">
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Período personalizado</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Data inicial</label>
                    <input
                      type="date"
                      className="w-full text-sm p-2 border rounded bg-background"
                      onChange={(e) => setDataInicial(e.target.value ? new Date(e.target.value) : undefined)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Data final</label>
                    <input
                      type="date"
                      className="w-full text-sm p-2 border rounded bg-background"
                      onChange={(e) => setDataFinal(e.target.value ? new Date(e.target.value) : undefined)}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setShowPersonalizado(false);
                      onFiltersChange({
                        ...filters,
                        periodo: { tipo: 'ultimos30' },
                      });
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    size="sm"
                    onClick={handlePersonalizadoApply}
                    disabled={!dataInicial || !dataFinal}
                  >
                    Aplicar Filtro
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <Plus className="h-4 w-4 text-muted-foreground" />
        </div>
      </Card>

      {/* Active Filters Summary */}
      {(filters.obras.length > 0 || filters.responsaveis.length > 0) && (
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-muted-foreground">Filtros ativos:</span>
          {filters.obras.length > 0 && (
            <Badge variant="outline" className="text-xs">
              {filters.obras.length} obras
            </Badge>
          )}
          {filters.responsaveis.length > 0 && (
            <Badge variant="outline" className="text-xs">
              {filters.responsaveis.length} responsáveis
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};